import React, {
  useState,
  memo,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'antd';
import { Button, PopAlert } from 'appkit-react';

import { DragableTableFrom } from '@components';
import { columns } from './configs/columns.config';
import { FORM_KEYS } from './configs/form.keys';

import actions from '@flux/action';

const WRAPPER_KEY = FORM_KEYS.TABLE_WRAPPER.key;

const GuidingPrinciples = forwardRef(
  (
    {
      _goBack,
      fetchAction = actions.fetchGudingPrincipleTemplate,
      saveAction = actions.saveGuidingPrincipleTemplate,
      gdPrcplList,
    },
    ref,
  ) => {
    useImperativeHandle(ref, () => ({
      save: handleSubmitForm,
    }));
    const [actionLoading, toggleActionLoading] = useState(false);
    const [error, setError] = useState(false);
    const data = useSelector(
      ({ systemSettings: { guidingPrincipleData }, insights }) => {
        if (gdPrcplList) {
          return gdPrcplList;
        }
        return guidingPrincipleData;
      },
    );
    const [form] = Form.useForm();
    const history = useHistory();
    const dispatch = useDispatch();

    const fieldMethods = useRef({});
    const registerFieldsMethods = (methods) => {
      fieldMethods.current = methods;
    };

    const goBack = () => {
      if (_goBack) {
        _goBack();
        return;
      }
      history.replace('/settings');
    };

    const handleSortEnd = ({ newIndex, oldIndex }) => {
      const { move } = fieldMethods.current;

      if (move) {
        move(oldIndex, newIndex);
      }
    };

    function checkNonEmptyText(inputArray) {
      // Iterate over each item in the array
      for (const item of inputArray) {
        // Check if the item has a 'title' property and if 'title' has a 'blocks' property
        if (item.title && Array.isArray(item.title.blocks)) {
          // Ensure 'blocks' has only one element
          if (item.title.blocks.length === 1) {
            // Get the first element of 'blocks'
            const block = item.title.blocks[0];

            // Check if the block has a 'text' property
            if (block.hasOwnProperty('text')) {
              // If 'text' is an empty string, return false
              if (block.text === '') {
                return false;
              }
            }
          }
        }
      }
      // If no items have an empty 'text' property, return true
      return true;
    }

    function convertToJson(inputArray) {
      return inputArray.map((item) => {
        // Create a new object to store the potentially converted properties
        const newItem = { ...item };

        // List of properties to check and convert if needed
        const propertiesToConvert = ['title', 'guidingPrinciple'];

        propertiesToConvert.forEach((property) => {
          if (typeof newItem[property] === 'string') {
            try {
              // Attempt to parse the string as JSON
              newItem[property] = JSON.parse(newItem[property]);
            } catch (e) {
              console.error(`Failed to parse property ${property}:`, e);
            }
          }
        });

        return newItem;
      });
    }

    const handleSave = async (e) => {
      toggleActionLoading(true);

      const convertedValue = convertToJson(e.guidingPrincipleList);

      // console.log('GUIP convertedValue: ', convertedValue);

      let result;

      if (checkNonEmptyText(convertedValue)) {
        setError(false);
        result = await saveAction(e);
      } else {
        setError(true);
        toggleActionLoading(false);
      }

      if (result?.success) {
        goBack();
        PopAlert({
          className: 'success-message-popover',
          oneLineDisplay: true,
          duration: 2000,
          status: 'success',
          message: 'SAVED SUCCESSFULLY',
        });
      }
      // toggleActionLoading(false);
    };

    const handleSubmitForm = async () => {
      await form.validateFields().then(handleSave);
    };

    useEffect(() => {
      dispatch(fetchAction());
    }, []);

    useEffect(() => {
      form.setFieldsValue({ [WRAPPER_KEY]: data });
    }, [data]);

    return (
      <div className="manage-module-wrapper system-settings-guiding-principle-wrapper">
        <Form
          form={form}
          scrollToFirstError
          autoComplete="off"
          className="integration-manage-form"
        >
          <div className="dragable-table-form">
            <DragableTableFrom
              registerFieldsMethods={registerFieldsMethods}
              onSortEnd={handleSortEnd}
              isActive
              formInstance={form}
              columns={columns}
              name={WRAPPER_KEY}
              isLoading={false}
              addBtnText="Add New Guiding Principle"
            />
          </div>
          {error ? (
            <div className="error-msg error-text">
              Title Fields Cannot be empty
            </div>
          ) : (
            ''
          )}

          <div className="action-wrapper">
            <Button
              size="xl"
              kind="secondary"
              onClick={goBack}
              isLoading={actionLoading}
            >
              Cancel
            </Button>
            <Button
              size="xl"
              onClick={handleSubmitForm}
              isLoading={actionLoading}
            >
              Save
            </Button>
          </div>
        </Form>
      </div>
    );
  },
);

export default memo(GuidingPrinciples);
